import GdprProvider from '@/Components/GDPR/GdprProvider';
import GaphenLoadingMask from '@/Components/LoadingMask';
import Chatbot from '@/Components/Main/Chatbot';
import Footer from '@/Components/Main/Footer';
import Navbar from '@/Components/Main/Navbar';
import { Button } from '@/Components/ui/button';
import { Toaster } from '@/Components/ui/toaster';
import { cn } from '@/lib/utils';
import { usePage } from '@inertiajs/react';
import { ArrowUp } from 'lucide-react';
import { useEffect, useState } from 'react';

// ScrollProgressButton Component
const ScrollProgressButton = () => {
    const [scrollProgress, setScrollProgress] = useState(0);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const totalHeight =
                document.documentElement.scrollHeight - window.innerHeight;
            const currentProgress = (window.scrollY / totalHeight) * 100;

            setScrollProgress(Math.min(currentProgress, 100));
            setIsVisible(window.scrollY > 400);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <Button
            onClick={scrollToTop}
            variant="outline"
            size="icon"
            className={cn(
                'fixed bottom-8 left-8 h-12 w-12 rounded-full border-2 bg-white/80 backdrop-blur-sm',
                'transition-all duration-300 hover:scale-110 hover:bg-white',
                'dark:border-emerald-600/20 dark:bg-gray-950/80 dark:hover:bg-gray-950',
                'shadow-lg hover:shadow-emerald-500/20 dark:shadow-emerald-500/10',
                isVisible
                    ? 'translate-y-0 opacity-100'
                    : 'pointer-events-none translate-y-10 opacity-0',
            )}
            aria-label="Back to top"
        >
            <div className="relative h-full w-full">
                {/* Progress Circle */}
                <svg
                    className="absolute h-full w-full -rotate-90 transform"
                    viewBox="0 0 100 100"
                >
                    {/* Background circle */}
                    <circle
                        cx="50"
                        cy="50"
                        r="45"
                        className="fill-none stroke-emerald-200 stroke-[4] dark:stroke-emerald-900"
                    />
                    {/* Progress circle */}
                    <circle
                        cx="50"
                        cy="50"
                        r="45"
                        className="fill-none stroke-emerald-600 stroke-[4] transition-all duration-200 dark:stroke-emerald-400"
                        strokeLinecap="round"
                        strokeDasharray={`${2 * Math.PI * 45}`}
                        strokeDashoffset={`${2 * Math.PI * 45 * (1 - scrollProgress / 100)}`}
                    />
                </svg>

                {/* Arrow Icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                    <ArrowUp
                        className={cn(
                            'h-6 w-6 transition-all duration-300',
                            'text-emerald-700 dark:text-emerald-400',
                            'transform-gpu',
                        )}
                    />
                </div>
            </div>
        </Button>
    );
};

interface LayoutProps {
    children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
    const { layout } = usePage().props as any;
    const [isLoading, setIsLoading] = useState(true);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Handle loading state based on complete page load
        const handleLoadComplete = () => {
            if (document.readyState === 'complete') {
                setIsLoading(false);
            }
        };

        if (document.readyState === 'complete') {
            setIsLoading(false);
        } else {
            window.addEventListener('load', handleLoadComplete);
        }

        // Handle navbar visibility with debounced scroll
        let lastScrollPosition = 0;
        let ticking = false;

        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    const currentScrollPosition = window.scrollY;
                    setIsVisible(
                        currentScrollPosition < lastScrollPosition ||
                            currentScrollPosition < 100,
                    );
                    lastScrollPosition = currentScrollPosition;
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('load', handleLoadComplete);
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <GdprProvider
            privacyPolicyUrl="/legal/privacy"
            consentVersion={layout?.gdpr?.consentVersion || '1.0'}
        >
            <div
                className={cn(
                    'flex min-h-screen flex-col',
                    'bg-gradient-to-br from-emerald-50 via-amber-50 to-emerald-100',
                    'dark:from-emerald-900/50 dark:via-amber-900/50 dark:to-emerald-800/50',
                    'transition-colors duration-300',
                )}
            >
                {isLoading && <GaphenLoadingMask />}

                {/* Sticky Header */}
                <div
                    className={cn(
                        'fixed left-0 right-0 top-0 z-50',
                        'transition-transform duration-300',
                        isVisible ? 'translate-y-0' : '-translate-y-full',
                    )}
                >
                    <Navbar
                        navLinks={layout.navbar.navLinks}
                        solutions={layout.navbar.solutions}
                        companyInfo={layout.navbar.companyInfo}
                        contactInfo={layout.navbar.contactInfo}
                    />
                </div>

                {/* Main Content */}
                <main className="flex-grow pt-20">{children}</main>

                {/* Footer */}
                <Footer
                    navLinks={layout.footer.navLinks}
                    companyInfo={layout.footer.companyInfo}
                    socialLinks={layout.footer.socialLinks}
                    stats={layout.footer.stats}
                    services={layout.footer.services}
                />

                {/* Global UI Elements */}
                <Toaster />
                <ScrollProgressButton />
                <Chatbot />
            </div>
        </GdprProvider>
    );
};

export default Layout;
